import { Fragment, useEffect, useRef, useState } from "react";
import CardDataRendererStyles from "./CardDataRenderer.module.scss";

import Container from "../Cards/Container/Container";
import Recommendations from "../Cards/Recommendations/Recommendations";
import UploadDoc from "../UploadDoc/UploadDoc";
import Loader from "../Loader/Loader";
import {
  bulkClauseApiProp,
  bulkClauseApiPropForLatestUpload,
  CLAUSE_CHECK,
  clauseCheckDocStatus,
  DASHBOARD_FEATURES,
  uploadStatus,
  viewAllBtnTooltipTextForClauseCheck,
  viewAllBtnTooltipTextForContractRedlining,
} from "../../modules/vscDashboard/constants";
import NoData from "../NoData/NoData";
import { RenderComponent } from "../../common/common";
import Button from "../Button/Button";
import Typography from "../Typography/Typography";
import { DASHBOARD_ENTITIES, noDataTextForKeyContractClause, ROUTES } from "../../common/constants";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar,
  Checkbox,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import uploadSvg from "../../assets/upload_blue.svg";
import Table from "../Table/Table";
import {
  reviewPendingApiProp,
  TableTypes,
} from "../../modules/ManageContract/constants";
import { useAppDispatch, useAppSelector } from "../../hooks/store";
import {
  getTableDataThunk,
  resetTableState,
  updateShowRecent,
} from "../../store/thunks/table.thunk";
import { getUnReviewedDocuments } from "../../modules/vscDashboard/utils";
import { getContractCountForStatus } from "../../services/common.service";
import {
  getClauseCheckThunk,
  getContractsReviewThunk,
  getLatestClauseCheckDataForDashboard,
} from "../../modules/vscDashboard/store/dashboard.thunk";
import { isUndefined } from "underscore";
import {
  archiveClauseCheckDocuments,
  archiveContractReviewDocuments,
} from "../../services/contracts.service";
import { getToast } from "../../common/utils";
import { MenuProps, Modal } from "antd";
import Dropdown from "../Dropdown/Dropdown";

const CardDataRenderer = (props: any) => {
  const {
    headerText,
    headerActions,
    user_docs,
    pending_documents,
    review_summary,
    loading,
    recommendedPercentage,
    hideGraph,
  } = props;
  const dispatch = useAppDispatch();
  const [openModal, setOpenModal] = useState(false);
  const isClauseCheck = headerText === CLAUSE_CHECK;
  const isContractRedlining =
    headerText === DASHBOARD_FEATURES.CONTRACT_REDLINING;
  const isEmpty = isClauseCheck
    ? pending_documents?.list?.length === 0 &&
      review_summary?.length === 1 &&
      review_summary[0].category === "Standard Clauses"
    : pending_documents?.list?.length === 0 &&
      review_summary?.length === 1 &&
      review_summary[0].category === "Recommendations";

  const { clauseCheck } = useAppSelector(
    (state) => state.rootReducer.vscDashboardReducer
  );
  const tableData = useAppSelector(
    (store) => store?.rootReducer?.tableInfoReducer?.data
  );

  const profileData = useAppSelector(
    (store) => store?.rootReducer?.userInfoReducer?.profileData?.data
  );
  const clauseCheckData = clauseCheck.pending_documents.list;
  const isClauseCheckAllowed = profileData?.isClauseCheckAllowed;
  const interval = useRef<any>();
  const [searchParams] = useSearchParams();
  const [isViewAllChecked, setIsViewAllChecked] = useState(true);
  const [contractCount, setContractCount] = useState<any>({
    totalCount: 0,
    pendingCount: 0,
    completedCount: 0,
  });

  const showRecentValue = tableData?.showRecent;
  const showRecent = isUndefined(showRecentValue) ? true : showRecentValue;

  const getTotalAndPendingCount = async () => {
    let totalCount = 0;
    var pendingCount = 0;
    const res = await getContractCountForStatus();
    const data = res?.data;
    data?.forEach((item: any) => {
      if (item?.status === clauseCheckDocStatus.CLAUSE_CHECK_READY) {
        pendingCount += item?.total;
      }
      if (item?.status !== clauseCheckDocStatus.CLAUSE_CHECK_COMPLETED) {
        totalCount += item?.total;
      }
    });

    setContractCount({
      ...contractCount,
      totalCount: totalCount,
      pendingCount: pendingCount,
    });
  };

  useEffect(() => {
    if (!isClauseCheckAllowed) return;
    const areNonReviewedFilesPresent = getUnReviewedDocuments(clauseCheckData);

    if (
      !clauseCheck.uploadProcessCompleted ||
      !areNonReviewedFilesPresent ||
      contractCount?.pendingCount < contractCount?.totalCount
    ) {
      clearInterval(interval.current);
    }

    if (areNonReviewedFilesPresent && clauseCheck.uploadProcessCompleted) {
      interval.current = setInterval(() => {
        // Use the actual showRecent state from Redux store instead of isViewAllChecked
        dispatch(
          getTableDataThunk(
            TableTypes.BULK_CLAUSE_DASHBOARD,
            1,
            tableData?.[TableTypes.BULK_CLAUSE_DASHBOARD]?.toBeRetrieved,
            showRecent
              ? bulkClauseApiPropForLatestUpload
              : bulkClauseApiProp
          )
        );
        getTotalAndPendingCount();
        dispatch(getLatestClauseCheckDataForDashboard());
      }, 5000);
    }

    return () => {
      clearInterval(interval.current);
    };
  }, [clauseCheckData, dispatch, clauseCheck.uploadProcessCompleted, showRecent]);

  useEffect(() => {
    isClauseCheckAllowed && getTotalAndPendingCount();
  }, [isClauseCheckAllowed]);

  return (
    <Container
      className={`${CardDataRendererStyles.clauseCheckContainer} ${
        !isClauseCheck ? CardDataRendererStyles.customContainer : ""
      }`}
    >
      <Fragment>
        {isClauseCheck ? (
          <Fragment>
            <Header
              actions={headerActions}
              headerText={headerText}
              openModal={openModal}
              setOpenModal={setOpenModal}
              isClauseCheck={isClauseCheck}
              userDocs={user_docs}
              entity={TableTypes.BULK_CLAUSE_DASHBOARD}
              contractCount={contractCount}
            />
            <div className={CardDataRendererStyles.contractReviewMetrics}>
              {loading ? (
                <Loader hideDescription={true} />
              ) : isEmpty ? (
                <NoData
                  withBorder={false}
                  text={noDataTextForKeyContractClause}
                />
              ) : (
                <div className={CardDataRendererStyles.contractTableContainer}>
                  <Table
                    entity={TableTypes.BULK_CLAUSE_DASHBOARD}
                    apiProps={
                      showRecent
                        ? bulkClauseApiPropForLatestUpload
                        : bulkClauseApiProp
                    }
                  />
                </div>
              )}
            </div>
          </Fragment>
        ) : (
          <div
            className={CardDataRendererStyles.contractRedliningContent}
            style={{
              display: "flex",
              flexDirection: "row",
              gap: "1rem",
              width: "100%",
            }}
          >
            <div
              style={{
                flex: 0.65,
                background: "#ffffff",
                padding: "24px",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Header
                actions={headerActions}
                headerText={user_docs[0].text}
                openModal={openModal}
                setOpenModal={setOpenModal}
                isClauseCheck={isClauseCheck}
                isContractRedlining={isContractRedlining}
                userDocs={[user_docs[0]]}
                isPrimary={true}
                entity={TableTypes.REVIEW_PENDING_DASHBOARD}
              />
              <div className={CardDataRendererStyles.contractReviewMetrics}>
                {loading ? (
                  <Loader hideDescription={true} />
                ) : isEmpty ? (
                  <NoData
                    withBorder={false}
                    text={noDataTextForKeyContractClause}
                  />
                ) : (
                  <div className={CardDataRendererStyles.listViewContainer}>
                    <Table
                      entity={TableTypes.REVIEW_PENDING_DASHBOARD}
                      apiProps={reviewPendingApiProp}
                    />
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                flex: 0.35,
                background: "#ffffff",
                padding: "24px",
                borderRadius: "8px",
                display: "flex",
                flexDirection: "column",
                gap: "16px",
              }}
            >
              <Header
                actions={headerActions}
                headerText={user_docs[1].text}
                openModal={openModal}
                setOpenModal={setOpenModal}
                isClauseCheck={isClauseCheck}
                isContractRedlining={isContractRedlining}
                userDocs={[user_docs[1]]}
                entity={TableTypes.REVIEW_PENDING_DASHBOARD}
                isPrimary={false}
              />
              <div className={CardDataRendererStyles.contractReviewMetrics}>
                {loading ? (
                  <Loader hideDescription={true} />
                ) : isEmpty ? (
                  <NoData
                    withBorder={false}
                    text={noDataTextForKeyContractClause}
                  />
                ) : (
                  <Fragment>
                    {RenderComponent(Recommendations, review_summary, {
                      data: user_docs[1]?.count === 0 ? [] : review_summary,
                      recommendedPercentage: recommendedPercentage,
                      hideGraph: user_docs[1]?.count === 0,
                      isClauseCheck: isClauseCheck,
                      loading: loading,
                    })}
                  </Fragment>
                )}
              </div>
            </div>
          </div>
        )}
      </Fragment>
    </Container>
  );
};

const Header = ({
  headerText,
  actions,
  openModal,
  setOpenModal,
  isClauseCheck,
  userDocs,
  isContractRedlining,
  isPrimary,
  contractCount,
  entity,
}: any) => {
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const handleOnClick = (action: string) => {
    switch (action) {
      case "Upload":
        setOpenModal(true);
        break;

      default:
        break;
    }
  };
  const documentsCondition =
    contractCount?.pendingCount < contractCount?.totalCount;

  const isTTC = headerText === DASHBOARD_FEATURES.TALK_TO_CONTRACT;

  const tableData = useAppSelector(
    (store) => store?.rootReducer?.tableInfoReducer
  )?.data;

  const showRecentValue = tableData?.showRecent;
  const showRecent = isUndefined(showRecentValue) ? true : showRecentValue;
  const tableDataInData = tableData?.[entity]?.tableData;
  const tableDataCount = tableDataInData?.count;

  const page = tableData?.[entity]?.page || 1;
  const count = tableData?.[entity]?.toBeRetrieved || 10;

  const reTriggerApi = () => {
    dispatch(
      getTableDataThunk(entity, page, count, {
        doc_status: TableTypes.REVIEW_PENDING,
      })
    );
  };
  const renderActions = actions?.filter(
    (action: any) => action.disabled !== true
  );

  const handleDocumentsDeletion = () => {
    setConfirmLoading(true);
    const api = isClauseCheck
      ? archiveClauseCheckDocuments
      : archiveContractReviewDocuments;
    api()
      .then((res) => {
        getToast("Success", "Documents deleted successfully");
        dispatch(
          isClauseCheck
            ? getClauseCheckThunk({ polling: false })
            : getContractsReviewThunk()
        );
      })
      .catch(() => {
        getToast("Error", "Something went wrong");
      })
      .finally(() => {
        setConfirmLoading(false);
        setOpenConfirmationModal(false);
        // Reseting the state for tables on deleting files
        dispatch(
          resetTableState(
            isClauseCheck
              ? TableTypes.BULK_CLAUSE_DASHBOARD
              : TableTypes.REVIEW_PENDING_DASHBOARD
          )
        );
      });
  };

  const ThreeDotsDropdownOptions: MenuProps["items"] = [
    {
      key: "deleteAllDocs",
      label: (
        <div className={CardDataRendererStyles.dropdownOption}>
          <span>Reset</span>
        </div>
      ),
      onClick: () => {
        setOpenConfirmationModal(true);
      },
    },
  ];

  const getDropdownOptions = (actionBtnText: string) => {
    switch (actionBtnText) {
      case "ThreeDots":
        return ThreeDotsDropdownOptions;
      default:
        return [];
    }
  };

  const handleSummaryClick = () => {
    if (isClauseCheck) {
      navigate(`${ROUTES.clauseSummary}?state=` + uploadStatus.LATEST);
    } else if (isContractRedlining) {
      navigate(ROUTES.manageContracts);
    }
  };

  const handleHomeStorageClick = () => {
    if (isClauseCheck) {
      navigate(ROUTES.bulkClauseCheck);
    } else if (isContractRedlining) {
      navigate(ROUTES.manageContracts);
    }
  };

  const updateTableData = async (e: any) => {
    const { checked } = e.target;
    dispatch(
      updateShowRecent({
        entity: TableTypes.BULK_CLAUSE_DASHBOARD,
        showRecent: checked,
      })
    );
    isClauseCheck &&
      (await dispatch(
        getTableDataThunk(
          entity,
          1,
          tableData?.[entity]?.toBeRetrieved,
          checked ? bulkClauseApiPropForLatestUpload : bulkClauseApiProp
        )
      ));
  };

  const renderHeaderFragment = (doc: any, index: number) => {
    const avatarStyle = isPrimary
      ? {
          border: "1px solid #E6A800",
          color: "#E6A800",
          background: "#E6A8000D",
        }
      : {
          border: "1px solid #0E9D58",
          color: "#0E9D58",
          background: "#0E9D580D",
        };

    return (
      <div key={index} className={CardDataRendererStyles.header}>
        <div className={CardDataRendererStyles.headerLeftSide}>
          <Avatar
            style={{
              ...avatarStyle,
              width: "42px",
              height: "42px",
              fontSize: "16px",
              fontWeight: "500",
              fontFamily: "Google Sans",
            }}
          >
            {isContractRedlining ? doc?.count : tableDataCount || doc?.count}
          </Avatar>
          <span className={CardDataRendererStyles.contractReviewText}>
            {doc.text}
            <p className={CardDataRendererStyles.subHeaderText}>{doc.descp}</p>
          </span>
        </div>
        {!isTTC && isPrimary && (
          <div className={CardDataRendererStyles.headerRightSide}>
            <div className={CardDataRendererStyles.contractRightHeader}>
              <div style={{ display: "flex", gap: "1rem" }}>
                <IconButton onClick={() => setOpenModal(true)}>
                  <img src={uploadSvg} alt="" height={16} width={16} />
                </IconButton>
              </div>
              <Tooltip
                title={
                  isClauseCheck
                    ? viewAllBtnTooltipTextForClauseCheck
                    : viewAllBtnTooltipTextForContractRedlining
                }
                placement="top"
                arrow
                slotProps={{
                  tooltip: {
                    sx: {
                      bgcolor: "common.black",
                      "& .MuiTooltip-arrow": {
                        color: "common.black",
                      },
                      fontFamily: "Google Sans",
                      fontSize: "12px",
                      fontWeight: 400,
                    },
                  },
                }}
              >
                <div style={{ display: "flex", gap: "1rem" }}>
                  <Button
                    style={{
                      borderRadius: "24px",
                      padding: "4px 16px",
                    }}
                    variant="outlined"
                    onClick={handleSummaryClick}
                  >
                    <Typography fontSize={12} fontWeight={400}>
                      {isContractRedlining ? "All Contracts" : "Summary"}
                    </Typography>
                  </Button>
                </div>
              </Tooltip>
              {renderActions?.map((action: any) => {
                const options = action?.renderOptions;
                const dropdownOptions = getDropdownOptions(action?.text);
                const actionComp = (
                  <div
                    className={CardDataRendererStyles.contractReviewActionBtn}
                    onClick={() => handleOnClick(action.text)}
                  >
                    <img
                      src={action.img}
                      alt=""
                      height={16}
                      width={16}
                    />
                  </div>
                );

                return options ? (
                  <Dropdown items={dropdownOptions}>{actionComp}</Dropdown>
                ) : (
                  actionComp
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      {isClauseCheck ? (
        <div className={CardDataRendererStyles.header}>
          <div className={CardDataRendererStyles.headerLeftSide}>
            <Avatar
              style={{
                border: "1px solid #0E9D58",
                color: "#0E9D58",
                background: "#0E9D580D",
                width: "42px",
                height: "42px",
                fontSize: "16px",
                fontWeight: "500",
                fontFamily: "Google Sans",
              }}
            >
              {tableDataCount || 0}
            </Avatar>
            <span className={CardDataRendererStyles.contractReviewText}>
              {headerText}
              <p className={CardDataRendererStyles.subHeaderText}>
                {isClauseCheck
                  ? "Number of bulk-uploaded contracts and checked for clause presence."
                  : ""}
              </p>
            </span>
          </div>
          <div className={CardDataRendererStyles.headerRightSide}>
            {!isTTC && (
              <div className={CardDataRendererStyles.contractRightHeader}>
                <div>
                  {documentsCondition && (
                    <div className={CardDataRendererStyles.loadingTag}>
                      <CircularProgress size={12} color="inherit" />
                      <span>
                        <Typography fontWeight={400} fontSize={10}>
                          Documents Reviewed {"("} {contractCount?.pendingCount}{" "}
                          {"/"} {contractCount?.totalCount} {")"}
                        </Typography>
                      </span>
                    </div>
                  )}
                </div>
                <div style={{ display: "flex", gap: "1rem" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <Checkbox
                      onChange={(e) => updateTableData(e)}
                      checked={showRecent}
                      size="small"
                    />
                    <Typography
                      fontSize={10}
                      fontWeight={400}
                      fontStyle={{ color: "#5C6063" }}
                    >
                      Show Recent
                    </Typography>
                  </div>
                  <IconButton onClick={() => setOpenModal(true)}>
                    <img src={uploadSvg} alt="" height={16} width={16} />
                  </IconButton>
                </div>
                <Tooltip
                  title={
                    isClauseCheck
                      ? viewAllBtnTooltipTextForClauseCheck
                      : viewAllBtnTooltipTextForContractRedlining
                  }
                  placement="top"
                  arrow
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: "common.black",
                        "& .MuiTooltip-arrow": {
                          color: "common.black",
                        },
                        fontFamily: "Google Sans",
                        fontSize: "12px",
                        fontWeight: 400,
                      },
                    },
                  }}
                >
                  <div style={{ display: "flex", gap: "1rem" }}>
                    <Button
                      style={{
                        borderRadius: "24px",
                        padding: "4px 16px",
                      }}
                      variant="outlined"
                      onClick={handleSummaryClick}
                    >
                      <Typography fontSize={12} fontWeight={400}>
                        {isContractRedlining ? "All Contracts" : "Summary"}
                      </Typography>
                    </Button>
                  </div>
                </Tooltip>
                {renderActions?.map((action: any) => {
                  const options = action?.renderOptions;
                  const dropdownOptions = getDropdownOptions(action?.text);
                  const actionComp = (
                    <div
                      className={CardDataRendererStyles.contractReviewActionBtn}
                      onClick={() => handleOnClick(action.text)}
                    >
                      <img
                        src={action.img}
                        alt=""
                        height={16}
                        width={16}
                      />
                    </div>
                  );

                  return options ? (
                    <Dropdown items={dropdownOptions}>{actionComp}</Dropdown>
                  ) : (
                    actionComp
                  );
                })}
              </div>
            )}
          </div>
        </div>
      ) : (
        userDocs.map((doc: any, index: number) =>
          renderHeaderFragment(doc, index)
        )
      )}
      <UploadDoc
        openModal={openModal}
        setOpenModal={setOpenModal}
        postUpload={reTriggerApi}
        dashboardEntity={
          isClauseCheck
            ? DASHBOARD_ENTITIES.CLAUSE_CHECK
            : DASHBOARD_ENTITIES.CONTRACT_REVIEW
        }
      />
      <Modal
        open={openConfirmationModal}
        title="Are you sure you want to delete all documents?"
        onCancel={() => setOpenConfirmationModal(false)}
        footer={[
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <Button
              variant="outlined"
              onClick={() => setOpenConfirmationModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleDocumentsDeletion}>Yes</Button>
          </div>,
        ]}
        centered
      >
        This action is irreversible.
      </Modal>
    </Fragment>
  );
};

export default CardDataRenderer;
